import type { ReactNode } from 'react';
import {
  getTaxNextStepFocusLabel,
  getTaxNextStepLabel,
  type TaxNextStepHandoffContext,
} from '../../lib/taxNextStepHandoff';

interface TaxNextStepArrivalRailProps {
  handoff: TaxNextStepHandoffContext;
  title?: ReactNode;
  message?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export default function TaxNextStepArrivalRail({
  handoff,
  title,
  message,
  action,
  className = '',
}: TaxNextStepArrivalRailProps) {
  return (
    <section className={`rounded-xl border border-gray-800 bg-gray-950/50 p-4 md:p-5 ${className}`.trim()}>
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-300">
            Tax next-step handoff
          </p>
          <div className="space-y-1">
            <h2 className="text-base font-semibold text-white">
              {title ?? `Continue with ${getTaxNextStepLabel(handoff.step).toLowerCase()}`}
            </h2>
            <p className="text-sm text-gray-300">
              {message ?? handoff.proofLabel}
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-gray-800 bg-gray-900/70 px-3 py-2 md:min-w-[12rem]">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">Dominant step</p>
          <p className="mt-1 text-sm font-semibold text-white">{getTaxNextStepLabel(handoff.step)}</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <div className="rounded-full border border-gray-700 bg-gray-950/50 px-3 py-1.5 text-xs text-gray-300">
          Focus:{' '}
          <span className="font-mono tabular-nums text-gray-100">
            {getTaxNextStepFocusLabel(handoff.focus)}
          </span>
        </div>
        <div className="rounded-full border border-gray-700 bg-gray-950/50 px-3 py-1.5 text-xs text-gray-300">
          Method:{' '}
          <span className="font-mono tabular-nums uppercase text-gray-100">{handoff.method}</span>
        </div>
        <div className="rounded-full border border-gray-700 bg-gray-950/50 px-3 py-1.5 text-xs text-gray-300">
          Jurisdiction:{' '}
          <span className="font-mono tabular-nums text-gray-100">{handoff.jurisdiction}</span>
        </div>
        {handoff.year !== undefined ? (
          <div className="rounded-full border border-gray-700 bg-gray-950/50 px-3 py-1.5 text-xs text-gray-300">
            Year:{' '}
            <span className="font-mono tabular-nums text-gray-100">{handoff.year}</span>
          </div>
        ) : null}
        <div className="rounded-full border border-gray-700 bg-gray-950/50 px-3 py-1.5 text-xs text-gray-300">
          Currency:{' '}
          <span className="font-mono tabular-nums text-gray-100">{handoff.currency}</span>
        </div>
        {handoff.proofCount !== undefined ? (
          <div className="rounded-full border border-gray-700 bg-gray-950/50 px-3 py-1.5 text-xs text-gray-300">
            Proof count:{' '}
            <span className="font-mono tabular-nums text-gray-100">{handoff.proofCount}</span>
          </div>
        ) : null}
        {handoff.proofValue ? (
          <div className="rounded-full border border-gray-700 bg-gray-950/50 px-3 py-1.5 text-xs text-gray-300">
            Proof value:{' '}
            <span className="font-mono tabular-nums text-gray-100">{handoff.proofValue}</span>
          </div>
        ) : null}
      </div>

      {action ? <div className="mt-4">{action}</div> : null}
    </section>
  );
}
